/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from 'react';
import { Col, Row, Form, Input, Button, Divider, InputNumber, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCatalogByName } from 'selectors/catalogos.selectors';
import PropTypes from 'prop-types';
import { validateFileList, validateMin } from 'utils/form/validators';
import { Upload, Accordion, AddButton } from 'components';
import { TIPO_MONITOREOS, ATRIBUTOS } from 'constants/nombresCatalogos';
import { MONITOREOS_INTERNET } from 'constants/tipoMonitoreos';
import { ReactComponent as IconReloj } from 'assets/images/monitoreos/SIMAC_Icono_reloj.svg';
import * as fieldMsgs from 'constants/formFieldsMessages';

import './Hallazgos.scss';

const Hallazgos = ({ form }) => {
  const tiposMonitoreo = useSelector(selectCatalogByName(TIPO_MONITOREOS));
  const tipoHallazgos = useMemo(
    () =>
      tiposMonitoreo.find((t) => t.idTipoMonitoreo === MONITOREOS_INTERNET)?.tipoHallazgos || [],
    [tiposMonitoreo]
  );

  const atributos = useSelector(selectCatalogByName(ATRIBUTOS));

  const findAtributoByName = (nombreAtributo) => {
    const idAtributo = atributos.find(
      (atributo) => atributo.atributo === nombreAtributo
    )?.idAtributo;
    return idAtributo?.toString();
  };

  const formComponent = ({ key, remove, name, disabled, isLastHallazgo, ...restField }) => (
    <div>
      <div className="formulario-hallazgos">
        <Row gutter={24}>
          <Col xs={12} className="formulario-hallazgos-tabla-header">
            Datos del hallazgo
          </Col>
          <Col xs={9} className="formulario-hallazgos-tabla-header">
            Muestras
          </Col>
          <Col xs={3} style={{ textAlign: 'center' }} className="formulario-hallazgos-tabla-header">
            Acciones
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="hallazgos-detalle">
            <Row gutter={24}>
              <Col xs={12}>
                <Form.Item shouldUpdate={(prevProps, newProps) => prevProps !== newProps}>
                  <Form.Item
                    {...restField}
                    name={[name, 'tipo_hallazgo']}
                    label="Tipo de hallazgo"
                    rules={[{ required: true, message: fieldMsgs.ERR_SELECT_FIELD_REQUIRED }]}>
                    <Select
                      placeholder="Selecciona"
                      fieldNames={{
                        value: 'idHallazgo',
                        label: 'nombreHallazgo'
                      }}
                      options={tipoHallazgos}
                      filterOption={(inputValue, option) =>
                        option.nombreHallazgo?.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                        -1
                      }
                      showSearch
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) => {
                  return (
                    prevValues.hallazgos[key]?.tipo_hallazgo !==
                    curValues.hallazgos[key]?.tipo_hallazgo
                  );
                }}>
                {() => {
                  const listaAtributos =
                    tipoHallazgos
                      .find(
                        (tipo) =>
                          tipo.idHallazgo === form.getFieldsValue()?.hallazgos?.[key]?.tipo_hallazgo
                      )
                      ?.atributos?.split(',') || [];
                  const atributosInputs = [];
                  if (listaAtributos.includes(findAtributoByName('cantidad'))) {
                    atributosInputs.push(
                      <Col xs={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'cantidad']}
                          label="Cantidad"
                          rules={[
                            { required: true, message: fieldMsgs.ERR_TEXT_FIELD_REQUIRED },
                            { validator: validateMin(0) }
                          ]}>
                          <Input placeholder="Cantidad" type="number" disabled={disabled} min={0} />
                        </Form.Item>
                      </Col>
                    );
                  }
                  if (
                    listaAtributos.includes(findAtributoByName('duracionHr')) ||
                    listaAtributos.includes(findAtributoByName('duracionMin')) ||
                    listaAtributos.includes(findAtributoByName('duracionSeg'))
                  ) {
                    atributosInputs.push(
                      <Form.Item label="Duración:">
                        <Col xs={24}>
                          <div className="div-reloj-campo-completo">
                            <div>
                              <IconReloj className="reloj-icon-completo" />
                            </div>
                            {listaAtributos.includes(findAtributoByName('duracionHr')) && (
                              <>
                                <Form.Item
                                  name={[name, 'duracionHr']}
                                  className="item-duracion-evento">
                                  <InputNumber
                                    className="reloj-input-completo"
                                    placeholder="00"
                                    min={0}
                                    max={24}
                                    keyboard
                                  />
                                </Form.Item>
                                <div className="div-p-time">
                                  <p>hrs.</p>
                                </div>
                              </>
                            )}
                            {listaAtributos.includes(findAtributoByName('duracionMin')) && (
                              <>
                                <Form.Item
                                  name={[name, 'duracionMin']}
                                  className="item-duracion-evento">
                                  <InputNumber
                                    className="reloj-input-completo"
                                    placeholder="00"
                                    min={0}
                                    max={60}
                                    keyboard
                                  />
                                </Form.Item>
                                <div className="div-p-time">
                                  <p>min.</p>
                                </div>
                              </>
                            )}
                            {listaAtributos.includes(findAtributoByName('duracionSeg')) && (
                              <>
                                <Form.Item
                                  name={[name, 'duracionSeg']}
                                  className="item-duracion-evento">
                                  <InputNumber
                                    className="reloj-input-completo"
                                    placeholder="00"
                                    min={0}
                                    max={60}
                                    keyboard
                                  />
                                </Form.Item>
                                <div className="div-p-time">
                                  <p>seg.</p>
                                </div>
                              </>
                            )}
                          </div>
                        </Col>
                      </Form.Item>
                    );
                  }
                  return atributosInputs;
                }}
              </Form.Item>
            </Row>
            <Form.Item {...restField} name={[name, 'lema']} label="Lema / Version">
              <Input.TextArea
                disabled={disabled}
                placeholder="Escribe.."
                rows={4}
                showCount
                maxLength={200}
              />
            </Form.Item>
            <Form.Item
              {...restField}
              name={[name, 'informacion_adicional']}
              label="Información adicional (modo, tiempo y lugar):">
              <Input.TextArea
                disabled={disabled}
                placeholder="Escribe..."
                rows={6}
                showCount
                maxLength={1200}
              />
            </Form.Item>
          </Col>
          <Col xs={9}>
            <div className="hallazgos-imagenes">
              <span className="subtitulo-descripcion">Captura de 2 a 5 muestras.</span>
              <div className="hallazgos-imagenes-row">
                <Form.Item
                  {...restField}
                  required
                  name={[name, 'muestra_1']}
                  label="Muestra 1"
                  className="hallazgos-imagenes-muestra"
                  rules={[
                    {
                      validator: validateFileList
                    }
                  ]}>
                  <Upload disabled={disabled} />
                </Form.Item>
                <Form.Item
                  required
                  {...restField}
                  name={[name, 'muestra_2']}
                  label="Muestra 2"
                  className="hallazgos-imagenes-muestra"
                  rules={[
                    {
                      validator: validateFileList
                    }
                  ]}>
                  <Upload disabled={disabled} />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, 'muestra_3']}
                  label="Muestra 3"
                  className="hallazgos-imagenes-muestra">
                  <Upload disabled={disabled} />
                </Form.Item>
              </div>
              <div className="hallazgos-imagenes-row">
                <Form.Item
                  {...restField}
                  name={[name, 'muestra_4']}
                  label="Muestra 4"
                  className="hallazgos-imagenes-muestra">
                  <Upload disabled={disabled} />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, 'muestra_5']}
                  label="Muestra 5"
                  className="hallazgos-imagenes-muestra">
                  <Upload disabled={disabled} />
                </Form.Item>
              </div>
            </div>
          </Col>
          <Col xs={3}>
            <div className="hallazgos-imagenes-acciones">
              <Button disabled={isLastHallazgo} style={{ height: '2em' }}>
                <DeleteOutlined onClick={() => remove(name)} />
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );

  return (
    <div className="modal-monitoreo-internet-hallazgos">
      <span className="seccion-hallazgos-titulo">Agrega los hallazgos necesarios.</span>
      <Form layout="vertical" form={form}>
        <Form.List name="hallazgos" initialValue={[{}]}>
          {(fields, { add, remove }) => {
            return (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const isLastHallazgo = fields.length <= 1;

                  const hallazgo = {
                    key: `hallazgo_${key}`,
                    label: `Hallazgo ${name + 1}`,
                    forceRender: true,
                    children: formComponent({
                      key,
                      remove,
                      name,
                      isLastHallazgo,
                      ...restField
                    })
                  };
                  return (
                    <Accordion key={key} items={[hallazgo]} defaultActiveKey={['hallazgo_0']} />
                  );
                })}
                <Divider />
                <div className="formulario-hallazgos-acciones">
                  <AddButton
                    onClick={() => {
                      add();
                    }}
                    style={{ marginRight: '1em' }}>
                    Agregar Nuevo
                  </AddButton>
                </div>
              </>
            );
          }}
        </Form.List>
      </Form>
    </div>
  );
};

Hallazgos.propTypes = {
  form: PropTypes.shape({
    getFieldsValue: PropTypes.func,
    setFieldsValue: PropTypes.func
  }).isRequired
};

export default Hallazgos;
